// import pacchetti
import { useState, useEffect } from "react";
import { useCollection } from "../../hooks/useCollection";

// import styles
import "./Project.css";

export default function ProjectFinancials({ prj }) {
  const { documents: analiticaData, error } = useCollection("analitica");
  const [filteredData, setFilteredData] = useState([]);
  const [totals, setTotals] = useState({
    ricavi: 0,
    materiali: 0,
    dipendenti: 0,
    lavorazioni: 0,
    struttura: 0,
    personale: 0,
    rappresentanza: 0,
    automezzi: 0,
    totaleCosti: 0,
    bilancio: 0,
  });

  // Filtra i dati analitica per settore del progetto e ordina per mese
  useEffect(() => {
    if (analiticaData && prj.codice) {
      const filtered = analiticaData
        .filter((item) => item.settore === prj.codice)
        .sort((a, b) => {
          // Prima ordina per anno, poi per mese
          const yearA = parseInt(a.anno) || 0;
          const yearB = parseInt(b.anno) || 0;
          if (yearA !== yearB) {
            return yearA - yearB;
          }
          const monthA = parseInt(a.mese) || 0;
          const monthB = parseInt(b.mese) || 0;
          return monthA - monthB;
        });
      setFilteredData(filtered);
    }
  }, [analiticaData, prj.codice]);

  // Calcola i totali
  useEffect(() => {
    if (filteredData.length > 0) {
      const newTotals = filteredData.reduce(
        (acc, item) => {
          acc.ricavi += parseFloat(item.ricavi) || 0;
          acc.materiali += parseFloat(item.materiali) || 0;
          acc.dipendenti += parseFloat(item.dipendenti) || 0;
          acc.lavorazioni += parseFloat(item.lavorazioni) || 0;
          acc.struttura += parseFloat(item.struttura) || 0;
          acc.personale += parseFloat(item.personale) || 0;
          acc.rappresentanza += parseFloat(item.rappresentanza) || 0;
          acc.automezzi += parseFloat(item.automezzi) || 0;
          return acc;
        },
        {
          ricavi: 0,
          materiali: 0,
          dipendenti: 0,
          lavorazioni: 0,
          struttura: 0,
          personale: 0,
          rappresentanza: 0,
          automezzi: 0,
        }
      );

      // Calcola totale costi e bilancio
      newTotals.totaleCosti =
        newTotals.materiali +
        newTotals.dipendenti +
        newTotals.lavorazioni +
        newTotals.struttura +
        newTotals.personale +
        newTotals.rappresentanza +
        newTotals.automezzi;

      newTotals.bilancio = newTotals.ricavi - newTotals.totaleCosti;

      setTotals(newTotals);
    }
  }, [filteredData]);

  // Formatta i numeri come valuta
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  if (error) {
    return (
      <div className="project-section">
        <h4>Analisi Finanziaria</h4>
        <p className="error">Errore nel caricamento dei dati: {error}</p>
      </div>
    );
  }

  return (
    <div className="project-section">
      <h4>Analisi Finanziaria - Settore: {prj.categoria}</h4>

      {filteredData.length === 0 ? (
        <p>Nessun dato analitico disponibile per questo settore.</p>
      ) : (
        <>
          {/* Tabella Riepilogo Bilancio */}
          <div className="financial-summary">
            <h5>Riepilogo Bilancio</h5>
            <table className="financial-table summary-table">
              <tbody>
                <tr className="revenue-row">
                  <td>
                    <strong>Ricavi Totali</strong>
                  </td>
                  <td className="amount positive">
                    {formatCurrency(totals.ricavi)}
                  </td>
                </tr>
                <tr className="cost-row">
                  <td>
                    <strong>Costi Totali</strong>
                  </td>
                  <td className="amount negative">
                    {formatCurrency(totals.totaleCosti)}
                  </td>
                </tr>
                <tr
                  className={`balance-row ${
                    totals.bilancio >= 0 ? "positive" : "negative"
                  }`}
                >
                  <td>
                    <strong>Bilancio</strong>
                  </td>
                  <td
                    className={`amount ${
                      totals.bilancio >= 0 ? "positive" : "negative"
                    }`}
                  >
                    {formatCurrency(totals.bilancio)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Tabella Dettaglio Costi */}
          <div className="cost-breakdown">
            <h5>Dettaglio Costi</h5>
            <table className="financial-table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Importo</th>
                  <th>% su Totale Costi</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Acquisto Materiali</td>
                  <td className="amount">{formatCurrency(totals.materiali)}</td>
                  <td>
                    {totals.totaleCosti > 0
                      ? ((totals.materiali / totals.totaleCosti) * 100).toFixed(
                          1
                        )
                      : 0}
                    %
                  </td>
                </tr>
                <tr>
                  <td>Spese per Dipendenti</td>
                  <td className="amount">
                    {formatCurrency(totals.dipendenti)}
                  </td>
                  <td>
                    {totals.totaleCosti > 0
                      ? (
                          (totals.dipendenti / totals.totaleCosti) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </td>
                </tr>
                <tr>
                  <td>Lavorazioni Esterne</td>
                  <td className="amount">
                    {formatCurrency(totals.lavorazioni)}
                  </td>
                  <td>
                    {totals.totaleCosti > 0
                      ? (
                          (totals.lavorazioni / totals.totaleCosti) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </td>
                </tr>
                <tr>
                  <td>Costi Struttura</td>
                  <td className="amount">{formatCurrency(totals.struttura)}</td>
                  <td>
                    {totals.totaleCosti > 0
                      ? ((totals.struttura / totals.totaleCosti) * 100).toFixed(
                          1
                        )
                      : 0}
                    %
                  </td>
                </tr>
                <tr>
                  <td>Costo del Personale</td>
                  <td className="amount">{formatCurrency(totals.personale)}</td>
                  <td>
                    {totals.totaleCosti > 0
                      ? ((totals.personale / totals.totaleCosti) * 100).toFixed(
                          1
                        )
                      : 0}
                    %
                  </td>
                </tr>
                <tr>
                  <td>Spese di Rappresentanza</td>
                  <td className="amount">
                    {formatCurrency(totals.rappresentanza)}
                  </td>
                  <td>
                    {totals.totaleCosti > 0
                      ? (
                          (totals.rappresentanza / totals.totaleCosti) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </td>
                </tr>
                <tr>
                  <td>Costi Automezzi</td>
                  <td className="amount">{formatCurrency(totals.automezzi)}</td>
                  <td>
                    {totals.totaleCosti > 0
                      ? ((totals.automezzi / totals.totaleCosti) * 100).toFixed(
                          1
                        )
                      : 0}
                    %
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Tabella Dati Dettagliati */}
          <div className="detailed-data">
            <h5>Dati Dettagliati ({filteredData.length} record)</h5>
            <div className="table-container">
              <table className="financial-table detailed-table">
                <thead>
                  <tr>
                    <th>Mese</th>
                    <th>Anno</th>
                    <th>Ricavi</th>
                    <th>Materiali</th>
                    <th>Dipendenti</th>
                    <th>Lavorazioni</th>
                    <th>Struttura</th>
                    <th>Personale</th>
                    <th>Rappresentanza</th>
                    <th>Automezzi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{item.mese}</td>
                      <td>{item.anno}</td>
                      <td className="amount positive">
                        {formatCurrency(parseFloat(item.ricavi) || 0)}
                      </td>
                      <td className="amount">
                        {formatCurrency(parseFloat(item.materiali) || 0)}
                      </td>
                      <td className="amount">
                        {formatCurrency(parseFloat(item.dipendenti) || 0)}
                      </td>
                      <td className="amount">
                        {formatCurrency(parseFloat(item.lavorazioni) || 0)}
                      </td>
                      <td className="amount">
                        {formatCurrency(parseFloat(item.struttura) || 0)}
                      </td>
                      <td className="amount">
                        {formatCurrency(parseFloat(item.personale) || 0)}
                      </td>
                      <td className="amount">
                        {formatCurrency(parseFloat(item.rappresentanza) || 0)}
                      </td>
                      <td className="amount">
                        {formatCurrency(parseFloat(item.automezzi) || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
